import { useState } from 'react';
import { useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta, mxn } from '../formato';

export interface ContenidoAsistido {
  cuentaId: string;
  nombre: string;
  canal: string;
  nombreCanal: string;
  titulo: string;
  descripcion: string;
  precioMxn: string;
  fotos: string[];
  enlace: string;
  destinos: string[];
}
export interface TareaCanal {
  id: string;
  tipo: 'publicar' | 'retirar';
  publicacionId: string;
  canal: string;
  nombreCanal: string;
  destino: string | null;
  contenido: Record<string, unknown>;
  motivo: string | null;
  estado: 'pendiente' | 'hecha';
  url: string | null;
  creadoEn: string;
}

const copiar = async (texto: string) => {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
};

/**
 * Publicación asistida (arquitectura 2.15): para los canales sin API la plataforma deja el título,
 * la descripción con el enlace del canal, el precio y las fotos listos para copiar, y una tarea
 * por cada grupo o portal; nunca automatiza el navegador del lotero.
 */
export function PublicacionAsistida({ publicacionId }: { publicacionId: string }) {
  const lista = useConsulta<ContenidoAsistido[]>(`/channels/canales/asistida/${publicacionId}`);
  const [aviso, setAviso] = useState<string | null>(null);
  if (lista.error) return <p className="error">{lista.error}</p>;
  if (!lista.datos) return <p className="subtitulo">Cargando…</p>;
  if (!lista.datos.length)
    return (
      <p className="vacio">
        Sin cuentas asistidas (grupos de Facebook, Marketplace, portales). Agrégalas en Canales.
      </p>
    );
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {aviso && <p className="etiqueta exito">{aviso}</p>}
      {lista.datos.map((c) => (
        <article key={c.cuentaId} className="tarjeta" style={{ display: 'grid', gap: 8 }}>
          <div className="rotulo">
            {c.nombreCanal} · {c.nombre}
          </div>
          <strong>{c.titulo}</strong>
          <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit', fontSize: 14 }}>
            {c.descripcion}
          </pre>
          <div className="nota">
            Precio {mxn(c.precioMxn)} · {c.fotos.length} fotos · publica en: {c.destinos.join(', ')}
          </div>
          <div className="acciones" style={{ flexWrap: 'wrap' }}>
            <button
              className="boton primario"
              onClick={() =>
                void copiar(`${c.titulo}\n\n${c.descripcion}`).then((ok) =>
                  setAviso(ok ? 'Texto copiado' : 'No se pudo copiar: selecciónalo a mano'),
                )
              }
            >
              Copiar texto
            </button>
            <button
              className="boton"
              onClick={() =>
                void copiar(c.enlace).then((ok) =>
                  setAviso(ok ? 'Enlace copiado' : 'No se pudo copiar'),
                )
              }
            >
              Copiar enlace con código {c.enlace.split('?c=')[1]}
            </button>
            {c.fotos.slice(0, 6).map((f) => (
              <a key={f} className="boton" href={f} target="_blank" rel="noreferrer">
                Foto
              </a>
            ))}
          </div>
        </article>
      ))}
    </div>
  );
}

/** Tareas pendientes de los canales asistidos: publicar donde falta y retirar al vender. */
export function TareasCanales({ publicacionId }: { publicacionId?: string }) {
  const api = useApi();
  const tareas = useConsulta<TareaCanal[]>(
    `/channels/canales/tareas?estado=pendiente${publicacionId ? `&publicacionId=${publicacionId}` : ''}`,
  );
  const [error, setError] = useState<string | null>(null);
  const hecha = async (t: TareaCanal) => {
    const url = t.tipo === 'publicar' ? window.prompt('Liga de la publicación (opcional)') : null;
    setError(null);
    try {
      await api(
        'POST',
        `/channels/canales/tareas/${t.id}/hecha`,
        url?.trim() ? { url: url.trim() } : {},
      );
      tareas.recargar();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  if (tareas.error) return <p className="error">{tareas.error}</p>;
  if (!tareas.datos) return <p className="subtitulo">Cargando…</p>;
  if (!tareas.datos.length) return <p className="vacio">Sin tareas pendientes en tus canales.</p>;
  return (
    <>
      {error && <p className="error">{error}</p>}
      <table>
        <thead>
          <tr>
            <th>Qué hacer</th>
            <th>Dónde</th>
            <th>Desde</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {tareas.datos.map((t) => (
            <tr key={t.id}>
              <td>
                <span className={`etiqueta ${t.tipo === 'retirar' ? 'peligro' : 'aviso'}`}>
                  {t.tipo === 'retirar' ? 'Retirar' : 'Publicar'}
                </span>{' '}
                {t.motivo ??
                  (typeof t.contenido['titulo'] === 'string' ? t.contenido['titulo'] : '')}
                {typeof t.contenido['url'] === 'string' && t.contenido['url'] && (
                  <div className="nota">
                    <a href={t.contenido['url'] as string} target="_blank" rel="noreferrer">
                      Ver publicación
                    </a>
                  </div>
                )}
              </td>
              <td>
                {t.nombreCanal}
                {t.destino ? ` · ${t.destino}` : ''}
              </td>
              <td>{fechaCorta(t.creadoEn)}</td>
              <td className="num">
                <button className="boton" onClick={() => void hecha(t)}>
                  Hecho
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
